import {useRequiredConfig} from '@domain/shifthandover'
import {FlexPage} from '@hconnect/common/components/FlexPage'
import {useCancelInactiveQueries} from '@hconnect/common/hooks/useCancelInactiveQueries'
import {useWidth} from '@hconnect/common/hooks/useWidth'
import {Repeat as RepeatIcon} from '@mui/icons-material'
import {Button, Grid, Link} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {
  defaultDesktop,
  desktopTasksFilterOptions,
  DesktopTasksFilterOptionsKeys,
  getFilterDifference,
  Page
} from '@widgets/shift-event-filters'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, Link as RouterLink, useParams} from 'react-router'

import {DoneTasksBox, OngoingTasksBox, PendingTasksBox} from '../containers/shiftEvents/TasksBox'
import {useFilterDatePicker} from '../hooks/useDatePicker'
import {DraggableTaskContextProvider} from '../hooks/useDraggableTask'
import {usePageFilters} from '../hooks/useEventsFilter'
import {useTrackAnalyticsEvent} from '../hooks/useTrackAnalyticsEvents'
import {EventsFilterBar} from '../layouts/FilterBar'
import {routeToRecurringTasks} from '../routes'
import {getActionContentSXWithFilter, getButtonOutlineWhite} from '../styles/common'

export const TasksPage: React.FC = () => {
  const {plantId} = useParams<{plantId: string}>()
  if (!plantId) {
    throw new Error('plantId is required parameter for TasksPage')
  }
  const {shifts, country} = useRequiredConfig(plantId)
  const [filterOptions, setFilters] = usePageFilters(plantId, Page.Tasks)
  const {getDateRange} = useFilterDatePicker(plantId, shifts)
  const {t} = useTranslation()
  const breakpoint = useWidth()
  const queryClient = useQueryClient()
  useCancelInactiveQueries(queryClient)
  const trackAnalyticsEvent = useTrackAnalyticsEvent({plantId, country})

  const [isMobileFilterOpen, openMobileFilter] = useState(false)
  const extendedFilterOptions = {...filterOptions}
  if (!extendedFilterOptions.timeRange) {
    extendedFilterOptions.timeRange = getDateRange()
  }

  const isMobile = breakpoint === 'xs'
  const isSmall = isMobile || breakpoint === 'sm'

  return (
    <FlexPage
      appName="Cockpit"
      title={isMobileFilterOpen ? t('shiftEvent.action.filter') : t('tasks.pageName')}
      sxActionContent={getActionContentSXWithFilter(isMobileFilterOpen)}
      headerActionContent={
        <Grid spacing={1} container alignItems="center" justifyContent="flex-end" wrap="nowrap">
          <Grid item xs={isMobileFilterOpen ? 12 : undefined}>
            <EventsFilterBar<DesktopTasksFilterOptionsKeys>
              activeSettings={filterOptions}
              isCollapsible={isSmall}
              options={desktopTasksFilterOptions}
              onChange={(nextFilterOptions) => {
                setFilters(nextFilterOptions)
                trackAnalyticsEvent('userAdjustsFilter', {
                  filterPage: Page.Tasks,
                  filterName: getFilterDifference(nextFilterOptions, filterOptions)
                })
              }}
              openMobileFilter={openMobileFilter}
              isMobileFilterOpen={isMobileFilterOpen}
              defaultFilters={defaultDesktop}
              plantId={plantId}
            />
          </Grid>
          {!isMobileFilterOpen && (
            <Grid item>
              <Link
                onClick={() =>
                  trackAnalyticsEvent('userMovesPage', {
                    pageFrom: t('tasks.pageName', {lng: 'en'}),
                    pageTo: t('recurringTasks.pageName', {lng: 'en'})
                  })
                }
                component={RouterLink}
                sx={{
                  textDecoration: 'none'
                }}
                to={generatePath(routeToRecurringTasks, {plantId})}
              >
                <Button
                  sx={getButtonOutlineWhite}
                  data-test-id="task-page-recurring-tasks-button"
                  variant="text"
                  startIcon={isMobile ? undefined : <RepeatIcon />}
                >
                  {isMobile ? <RepeatIcon /> : t('tasks.recurringTasks')}
                </Button>
              </Link>
            </Grid>
          )}
        </Grid>
      }
    >
      {!isMobileFilterOpen && (
        <DraggableTaskContextProvider plantId={plantId}>
          <Grid container spacing={2} sx={{height: {md: 640}}}>
            <Grid item xs={12} md={4} sx={{height: {md: '100%'}}}>
              <PendingTasksBox plantId={plantId} filterOptions={extendedFilterOptions} />
            </Grid>
            <Grid item xs={12} md={4} sx={{height: {md: '100%'}}}>
              <OngoingTasksBox plantId={plantId} filterOptions={extendedFilterOptions} />
            </Grid>
            <Grid item xs={12} md={4} sx={{height: {md: '100%'}}}>
              <DoneTasksBox plantId={plantId} filterOptions={extendedFilterOptions} />
            </Grid>
          </Grid>
        </DraggableTaskContextProvider>
      )}
    </FlexPage>
  )
}
